/**
 * Responsibility: show a whole set inline, with what is missing greyed out.
 *
 * A panel rather than a dialog. Opening a set is looking at a different part
 * of the collection, not interrupting what you were doing, so it belongs in
 * the page with its own tab - where you can still switch view, still filter,
 * and still leave by picking another tab rather than by dismissing something.
 *
 * **A set with no card list is not an error here.** Promo groupings grow
 * forever and sports cards are not in a Pokemon population file, so plenty of
 * sets have no trustworthy contents - and clicking their tile used to produce
 * a red failure. What it produces now is the cards you own from it and a line
 * saying why there is nothing to compare them against.
 */
import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import type { CollectionItem } from '../../api/client';
import { useSetDetail } from '../../api/hooks/useSets';

type Props = {
  label: string;
  /** Everything owned, so the fallback can still show this set's cards. */
  items: CollectionItem[];
};

type Showing = 'all' | 'missing';

/** Cards owned from this set, for when there is no list to line them up with. */
function ownedFrom(items: CollectionItem[], label: string): CollectionItem[] {
  return items.filter((item) => item.set_label === label);
}

export function FullSetPanel({ label, items }: Props) {
  const [showing, setShowing] = useState<Showing>('all');
  const detail = useSetDetail(label);

  const cards = useMemo(() => {
    const all = detail.data?.cards ?? [];
    return showing === 'missing' ? all.filter((card) => !card.held) : all;
  }, [detail.data, showing]);

  // **Null is not zero.** A set whose size cannot be established still has
  // cards in it, and rendering "11 of 0 - 0%" says the opposite of what is
  // true. The hub sends null for a promo bucket or an ambiguous name, and
  // the header then reports what is owned and stops.
  const total = detail.data?.total ?? null;
  const held = detail.data?.held ?? 0;
  const percent = total !== null && total > 0 ? Math.round((held / total) * 100) : null;

  if (detail.isLoading) return <LinearProgress />;

  // No trustworthy list. Show what is owned and say why, rather than failing.
  if (!detail.data) {
    const owned = ownedFrom(items, label);
    return (
      <Stack spacing={1.5} sx={{ minHeight: 0, flex: 1 }}>
        <Alert severity="info" icon={false}>
          No card list for <strong>{label}</strong>. It is either a grouping that
          grows forever, a card with no population data behind it, or a name too
          ambiguous to match safely &mdash; and a guessed list would show cards
          this set does not contain.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          {owned.length} {owned.length === 1 ? 'card' : 'cards'} owned from it.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ minHeight: 0, flex: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ flexShrink: 0 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {total === null
            ? `${held} ${held === 1 ? 'card' : 'cards'} owned · set size unknown`
            : `${held} of ${total} · ${percent}%`}
        </Typography>
        {detail.data.matched_name ? (
          <Chip size="small" variant="outlined" label={detail.data.matched_name} />
        ) : null}
        {detail.data.year ? (
          <Chip size="small" variant="outlined" label={String(detail.data.year)} />
        ) : null}
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={showing}
          onChange={(_, next: Showing | null) => next && setShowing(next)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="missing">Missing</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* No bar at all when the set size is unknown. A determinate bar needs
          a denominator, and drawing one at zero would say the collection is
          nowhere near done when nothing of the sort has been established. */}
      {percent === null ? null : (
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{ height: 8, borderRadius: 1, flexShrink: 0 }}
        />
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'grid',
          gap: 1,
          gridTemplateColumns: {
            xs: 'repeat(3, 1fr)',
            sm: 'repeat(5, 1fr)',
            md: 'repeat(8, 1fr)',
            lg: 'repeat(10, 1fr)',
          },
          alignContent: 'start',
        }}
      >
        {cards.map((card) => (
          <Box
            key={card.number}
            sx={{
              p: 0.75,
              borderRadius: 1,
              border: '1px solid',
              borderColor: card.held ? 'success.main' : 'divider',
              // Owned reads first; missing recedes rather than disappears,
              // because it is the thing still to find.
              opacity: card.held ? 1 : 0.45,
              minWidth: 0,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              #{card.number}
            </Typography>
            {/* An em dash rather than "unknown": the catalogue fills almost
                every name now, and what is left is a card nothing has
                recorded a name for - which is a gap, not a card called
                Unknown. The number above it already identifies it. */}
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: card.held ? 600 : 400,
                fontStyle: card.name ? 'normal' : 'italic',
              }}
            >
              {card.name || '—'}
            </Typography>
          </Box>
        ))}
      </Box>

      {cards.length === 0 ? (
        <Typography variant="body2" color="success.main" sx={{ flexShrink: 0 }}>
          Nothing missing. The whole set is here.
        </Typography>
      ) : null}
    </Stack>
  );
}
